import { useProjectRequestDownloadTokenLazyQuery } from '../types/graphqlTypes';

// Download a SAS-signed Azure URL via a hidden iframe — the browser fetches straight from blob
// storage and the page (and its WebGL context) is left untouched. Cleaned up after a minute.
const triggerUrlDownload = (url: string) => {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);
  setTimeout(() => document.body.removeChild(iframe), 60000);
};

export const useDownloadExport = () => {
  const [getDownloadToken, { loading }] = useProjectRequestDownloadTokenLazyQuery();

  const downloadExport = async ({ projectId, exportId }: { projectId: string; exportId: string; filename?: string }) => {
    if (!projectId) return;
    const { data: tokenData } = await getDownloadToken({ variables: { projectId } });

    const response = await fetch(`${import.meta.env.VITE_NX_SERVER}/project/${projectId}/export/${exportId}/download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: tokenData?.projectRequestDownloadToken }),
    });

    // Server returns SAS-signed Azure URLs ({ files: [{ url, fileName }] }); download each straight
    // from blob storage, staggered so browsers don't drop simultaneous downloads.
    const { files } = (await response.json()) as { files?: { url: string; fileName: string }[] };
    const fileList = files ?? [];
    for (let i = 0; i < fileList.length; i++) {
      triggerUrlDownload(fileList[i].url);
      if (i < fileList.length - 1) await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  return { downloadExport, loading: loading || false };
};
